import { buttonV2Class } from '@synoptic/ui-kit/button-v2/button-v2.css.js';
import {
  CardV2,
  CardV2Body,
  CardV2Description,
  CardV2Title,
} from '@synoptic/ui-kit/card-v2/card-v2.js';
import { ArrowRightIcon } from '@synoptic/ui-kit/icons/react/arrow-right.js';
import { useId, useState } from 'react';
import { Link } from '@/routes-utils/navigation.tsx';
import { paths } from '@/routes-utils/paths.ts';
import { customStreamTutorialVideo } from '@/routes/_app.p.learning-center/custom-stream-tutorial-video.ts';
import { LearningVideoModal } from '@/routes/_app.p.learning-center/learning-video-modal.tsx';
import buyUseCustomStreamCover from './buy-use-custom-stream-cover.webp';
import {
  exploreHowCardBodyV2,
  exploreHowCardGridV2,
  exploreHowCardTitleV2,
  exploreHowCardV2,
  exploreHowCoverImageV2,
  exploreHowCoverV2,
  exploreHowWatchButtonV2,
  exploreSectionHeaderV2,
  exploreSectionTitleV2,
  exploreSectionV2,
} from './explore-page-v2.css.ts';
import settingUpStreamDeckCover from './setting-up-stream-deck-cover.webp';
import setupAiFiltersCover from './setup-ai-filters-cover.webp';

type HowSynopticWorksCard = {
  id: string;
  title: string;
  description: string;
  s3Key: string;
  watchLabel: string;
  coverSrc: string;
};

const HOW_SYNOPTIC_WORKS_CARDS: readonly HowSynopticWorksCard[] = [
  {
    id: 'setting-up-stream-deck',
    title: 'Set up Newsdeck',
    description:
      'Create your first NewsDeck and add lists to organize your data feeds in one view.',
    s3Key: 'service/onboarding-videos/Set up stream deck.mp4',
    watchLabel: 'Watch 1:32',
    coverSrc: settingUpStreamDeckCover,
  },
  {
    id: 'set-up-stream-deck-ai-filters',
    title: 'Set up AI filters',
    description:
      'Configure AI-powered filters on your NewsDeck lists to surface the most relevant news.',
    s3Key: 'service/onboarding-videos/Set up AI Filters.mp4',
    watchLabel: 'Watch 1:54',
    coverSrc: setupAiFiltersCover,
  },
  {
    ...customStreamTutorialVideo,
    description:
      'Learn how to find, purchase, and start using custom data streams for your trading workflow.',
    watchLabel: 'Watch 02:00',
    coverSrc: buyUseCustomStreamCover,
  },
];

export const ExploreHowSynopticWorksSectionV2 = () => {
  const titleId = useId();
  const [openVideoId, setOpenVideoId] = useState<string | null>(null);
  const openVideo = HOW_SYNOPTIC_WORKS_CARDS.find(
    (card) => card.id === openVideoId,
  );

  return (
    <section className={exploreSectionV2} aria-labelledby={titleId}>
      <header className={exploreSectionHeaderV2}>
        <h2 id={titleId} className={exploreSectionTitleV2}>
          How Synoptic works
        </h2>
        <Link className={buttonV2Class.secondary} to={paths.learningCenter}>
          Go to Learning Center
          <ArrowRightIcon />
        </Link>
      </header>
      <div className={exploreHowCardGridV2}>
        {HOW_SYNOPTIC_WORKS_CARDS.map((card) => (
          <CardV2
            key={card.id}
            className={exploreHowCardV2}
            hoverable
            data-testid="how-synoptic-works-card-v2"
          >
            <div
              className={exploreHowCoverV2}
              data-testid="how-synoptic-works-cover-v2"
            >
              <img
                className={exploreHowCoverImageV2}
                src={card.coverSrc}
                alt=""
              />
            </div>
            <CardV2Body className={exploreHowCardBodyV2}>
              <CardV2Title className={exploreHowCardTitleV2}>
                {card.title}
              </CardV2Title>
              <CardV2Description>{card.description}</CardV2Description>
            </CardV2Body>
            <button
              type="button"
              className={exploreHowWatchButtonV2}
              onClick={() => setOpenVideoId(card.id)}
            >
              {card.watchLabel}
              <ArrowRightIcon />
            </button>
          </CardV2>
        ))}
      </div>
      <LearningVideoModal
        video={openVideo ?? null}
        onClose={() => setOpenVideoId(null)}
      />
    </section>
  );
};
